import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ChangeEventReason,
  ChangeEventType,
  EVENT_LABELS,
  REASON_LABELS,
  userSelectableReasons,
} from "@/lib/change-events";
import {
  changeEventCopy,
  multiChangeCopy,
  NOTES_HELPER,
  NOTES_HELPER_OPTIONAL,
  NOTES_LABEL,
} from "@/lib/change-event-copy";

export interface ChangeReasonPrompt {
  /** Stable key so callers can match results back to inputs. */
  key: string;
  eventType: ChangeEventType;
  /** Optional contextual hint (e.g. "from NVO → Manual"). */
  detail?: string;
  /** Old / new values used to build the title (e.g. trip type pivots). */
  fromLabel?: string | null;
  toLabel?: string | null;
}

export interface ChangeReasonResult {
  key: string;
  eventType: ChangeEventType;
  reason: ChangeEventReason;
  otherText: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** One or more events that need a reason in the same submit. */
  events: ChangeReasonPrompt[];
  /** Order unique id for header context. */
  orderUniqueId?: string | null;
  /** Headline override (defaults to "Reason required"). */
  title?: string;
  /** Body copy override (defaults to the canonical matrix copy). */
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isPending?: boolean;
  /**
   * Render the Notes textarea for every reason (not just "Other").
   * Notes remain REQUIRED only when the reason is "other".
   */
  alwaysShowNotes?: boolean;
  onConfirm: (results: ChangeReasonResult[]) => void;
}

interface RowState {
  reason: ChangeEventReason | "";
  otherText: string;
}

const isValid = (row: RowState) =>
  !!row.reason && (row.reason !== "other" || row.otherText.trim().length > 0);

export function ChangeReasonDialog({
  open,
  onOpenChange,
  events,
  orderUniqueId,
  title,
  description,
  confirmLabel,
  cancelLabel,
  isPending,
  alwaysShowNotes,
  onConfirm,
}: Props) {
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const single = events.length === 1 ? events[0] : null;
  const copy = single
    ? changeEventCopy(single.eventType, {
        orderUniqueId,
        fromLabel: single.fromLabel,
        toLabel: single.toLabel,
      })
    : multiChangeCopy({ orderUniqueId });

  useEffect(() => {
    if (open) {
      setRows(
        Object.fromEntries(events.map((e) => [e.key, { reason: "", otherText: "" }])),
      );
    }
  }, [open, events]);

  const allValid = events.every((e) => isValid(rows[e.key] ?? { reason: "", otherText: "" }));

  const handleConfirm = () => {
    onConfirm(
      events.map((e) => {
        const row = rows[e.key];
        const text = row.otherText.trim();
        return {
          key: e.key,
          eventType: e.eventType,
          reason: row.reason as ChangeEventReason,
          otherText:
            row.reason === "other" ? text : alwaysShowNotes && text ? text : null,
        };
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title ?? copy.title}</DialogTitle>
          <DialogDescription>{description ?? copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {events.map((evt) => {
            const row = rows[evt.key] ?? { reason: "", otherText: "" };
            const options = userSelectableReasons(evt.eventType);
            const eventCopy = changeEventCopy(evt.eventType, {
              orderUniqueId,
              fromLabel: evt.fromLabel,
              toLabel: evt.toLabel,
            });
            return (
              <div
                key={evt.key}
                className={
                  single ? "space-y-2" : "space-y-2 rounded-md border p-3"
                }
              >
                {!single && (
                  <div className="text-sm font-medium">
                    {EVENT_LABELS[evt.eventType]}
                    {evt.detail ? (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {evt.detail}
                      </span>
                    ) : null}
                  </div>
                )}
                {single && (evt.fromLabel || evt.toLabel) && (
                  <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">
                      {evt.fromLabel ?? "—"}
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">{evt.toLabel ?? "—"}</span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>
                    {eventCopy.reasonLabel}{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={row.reason}
                    onValueChange={(v) =>
                      setRows((prev) => ({
                        ...prev,
                        [evt.key]: { ...row, reason: v as ChangeEventReason },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((r) => (
                        <SelectItem key={r} value={r}>
                          {REASON_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(alwaysShowNotes || row.reason === "other") && (
                  <div className="space-y-2">
                    <Label>
                      {NOTES_LABEL}{" "}
                      {row.reason === "other" && (
                        <span className="text-destructive">*</span>
                      )}
                    </Label>
                    <Textarea
                      value={row.otherText}
                      onChange={(e) =>
                        setRows((prev) => ({
                          ...prev,
                          [evt.key]: { ...row, otherText: e.target.value },
                        }))
                      }
                      rows={2}
                      placeholder={
                        row.reason === "other" ? NOTES_HELPER : NOTES_HELPER_OPTIONAL
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {cancelLabel ?? copy.cancelLabel}
          </Button>
          <Button onClick={handleConfirm} disabled={!allValid || isPending}>
            {isPending ? "Saving…" : (confirmLabel ?? copy.confirmLabel)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}